import { compressImageFile } from '../../../utils/imageCompress';
import { useRef } from 'react';
import { Button, Dropdown, Input, InputNumber, Segmented, Space, Tooltip, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import { DeleteOutlined, DownOutlined, EditOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { appendSealedSignature, findSignatureSections, sealParty } from '../../../contract/signature';
import type { ContractSection, OverlayItem } from '../../../contract/types';
import { genId, SECTION_TYPE_LABELS } from '../../../contract/types';

interface Props {
  overlays: OverlayItem[];
  onChange: (overlays: OverlayItem[]) => void;
  /** 顶层区块，用来显示章挂在哪个区块上。 */
  sections?: ContractSection[];
  /**
   * 能改区块时，「添加印章」默认把章盖进签章区（跟着落款走，永不跑位）——
   * 和飞印宝一样把章当成内容流的一部分；自由摆放退成次选。
   */
  onSectionsChange?: (sections: ContractSection[]) => void;
}

/** 选了图之后往哪盖。 */
type SealTarget = { kind: 'free' } | { kind: 'append' } | { kind: 'party'; sectionId: string; partyId: string };

const SEAL_MAX_BYTES = 1024 * 1024;

/** 自由元素层管理：添加印章 / 签名区域，位置直接在上方预览里拖拽调整。 */
export function OverlaysEditor({ overlays, onChange, sections, onSectionsChange }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const targetRef = useRef<SealTarget>({ kind: 'free' });
  const signatures = sections && onSectionsChange ? findSignatureSections(sections) : [];
  const sealedParties = signatures.flatMap((sec) =>
    sec.parties.filter((p) => p.sealDataUrl).map((p) => ({ sec, party: p })),
  );
  const pickSeal = (t: SealTarget) => {
    targetRef.current = t;
    fileInputRef.current?.click();
  };
  const sectionLabel = (id?: string) => {
    const s = sections?.find((x) => x.id === id);
    if (!s) return '已删除的区块';
    const text = 'content' in s && typeof s.content === 'string' ? s.content.replace(/<[^>]+>/g, '').trim() : '';
    return text ? text.slice(0, 8) : SECTION_TYPE_LABELS[s.type];
  };

  const addSeal = (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      message.error('请上传 PNG/JPG 图片（推荐透明底 PNG 公章图）');
      return;
    }
    if (file.size > SEAL_MAX_BYTES) {
      message.error('印章图过大（>1MB），请压缩后再上传');
      return;
    }
    void compressImageFile(file).then((dataUrl) => {
      const t = targetRef.current;
      if (t.kind !== 'free' && sections && onSectionsChange) {
        onSectionsChange(
          t.kind === 'append' ? appendSealedSignature(sections, dataUrl) : sealParty(sections, t.sectionId, t.partyId, dataUrl),
        );
        message.success(
          t.kind === 'append'
            ? '已在末尾加上落款并盖章，章跟着落款走，明细多少行都不会跑位'
            : '已盖到落款上，章跟着落款走，明细多少行都不会跑位',
        );
        return;
      }
      onChange([
        ...overlays,
        // 默认 40mm 实务尺寸，先放右上区域，预览里拖到位
        // 跟着内容末尾走：压在签字区上，明细多几行章也跟着下去
        { kind: 'seal', id: genId(), dataUrl, xMm: 120, yMm: -45, wMm: 40, hMm: 40, anchor: 'content_end' },
      ]);
      message.success('印章已添加，在上方预览里拖到目标位置');
    });
  };

  const addSignArea = () => {
    const n = overlays.filter((o) => o.kind === 'sign_area').length;
    onChange([
      ...overlays,
      {
        kind: 'sign_area',
        id: genId(),
        label: `签署方${n + 1}`,
        xMm: 20 + n * 60,
        yMm: -40,
        anchor: 'content_end',
        wMm: 50,
        hMm: 20,
      },
    ]);
  };

  const patch = (id: string, p: Partial<OverlayItem>) => {
    onChange(overlays.map((o) => (o.id === id ? ({ ...o, ...p } as OverlayItem) : o)));
  };

  const remove = (id: string) => onChange(overlays.filter((o) => o.id !== id));

  const sealMenu: MenuProps['items'] = [
    ...(signatures.length
      ? signatures.flatMap((sec) =>
          sec.parties.map((p) => ({
            key: `party:${sec.id}:${p.id}`,
            label: `盖到落款「${p.label || '签署方'}」上${p.sealDataUrl ? '（替换原章）' : ''}`,
          })),
        )
      : [{ key: 'append', label: '在末尾加落款并盖章' }]),
    { type: 'divider' as const },
    { key: 'free', label: '自由摆放（压在任意文字上）' },
  ];
  const onSealMenu: MenuProps['onClick'] = ({ key }) => {
    if (key === 'free') pickSeal({ kind: 'free' });
    else if (key === 'append') pickSeal({ kind: 'append' });
    else {
      const [, sectionId, partyId] = String(key).split(':');
      pickSeal({ kind: 'party', sectionId, partyId });
    }
  };

  return (
    <div>
      <Space size={8} wrap style={{ marginBottom: overlays.length || sealedParties.length ? 10 : 0 }}>
        {onSectionsChange ? (
          <Dropdown menu={{ items: sealMenu, onClick: onSealMenu }} trigger={['click']}>
            <Button size="small" icon={<SafetyCertificateOutlined />}>
              添加印章 <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        ) : (
          <Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => pickSeal({ kind: 'free' })}>
            添加印章
          </Button>
        )}
        <Button size="small" icon={<EditOutlined />} onClick={addSignArea}>
          添加签名区域
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {onSectionsChange ? '章默认盖在落款上，跟着落款走；选「自由摆放」才需要在预览里拖' : '添加后在上方预览里拖动定位、拖右下角缩放'}
        </Typography.Text>
      </Space>

      {sealedParties.map(({ sec, party }) => (
        <div
          key={party.id}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: '6px 8px',
            border: '1px solid var(--border-soft)',
            borderRadius: 6,
            marginBottom: 6,
          }}
        >
          <img src={party.sealDataUrl!} alt="印章" style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>印章</span>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            盖在落款「{party.label || '签署方'}」上 · 跟着落款走
          </Typography.Text>
          <span style={{ flex: 1 }} />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => sections && onSectionsChange?.(sealParty(sections, sec.id, party.id, null))}
          />
        </div>
      ))}

      {overlays.map((o) => (
        <div
          key={o.id}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: '6px 8px',
            border: '1px solid var(--border-soft)',
            borderRadius: 6,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          {o.kind === 'seal' ? (
            <>
              <img
                src={o.dataUrl}
                alt="印章"
                style={{ width: 30, height: 30, objectFit: 'contain', flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, fontWeight: 600 }}>印章</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 13 }}>✍</span>
              <Input
                size="small"
                style={{ width: 110 }}
                value={o.label}
                onChange={(e) => patch(o.id, { label: e.target.value })}
                placeholder="签署方"
              />
            </>
          )}
          {/* 纵向基准：章要压在签字区上，而签字区随明细行数上下移 —— 默认跟着内容走 */}
          <Tooltip title="「跟随内容」：在画布上拖到哪个区块上，就跟着那个区块走，明细多少行都不跑位；「固定页顶」：钉在纸上某处，不随内容动">
            <Segmented
              size="small"
              value={o.anchor === 'content_end' || o.anchor === 'section' ? 'follow' : 'page'}
              options={[
                { label: '跟随内容', value: 'follow' },
                { label: '固定页顶', value: 'page' },
              ]}
              onChange={(v) =>
                patch(
                  o.id,
                  v === 'follow'
                    ? { anchor: 'content_end', anchorSectionId: undefined, yMm: -45 }
                    : { anchor: 'page', anchorSectionId: undefined, yMm: 40 },
                )
              }
            />
          </Tooltip>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {o.anchor === 'section'
              ? `跟随「${sectionLabel(o.anchorSectionId)}」`
              : o.anchor === 'content_end'
                ? '在内容末尾 · 拖到画布上的区块即跟随它'
                : `位置 ${o.xMm.toFixed(0)},${o.yMm.toFixed(0)}mm`}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>宽(mm)</Typography.Text>
          <InputNumber
            size="small"
            min={10}
            max={200}
            style={{ width: 64 }}
            value={Math.round(o.wMm)}
            onChange={(v) => v && patch(o.id, { wMm: v, ...(o.kind === 'seal' ? { hMm: v } : {}) })}
          />
          <span style={{ flex: 1 }} />
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(o.id)} />
        </div>
      ))}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          addSeal(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
